import { usePushNotificationContext } from '@context/PushNotificationContext';
import React from 'react';

import { usePwaInstall } from '../hooks/usePwaInstall';

// Custom events to coordinate with PwaInstallPrompt
const NOTIFICATION_PROMPT_VISIBLE_EVENT = 'notification-prompt-visible';
const NOTIFICATION_PROMPT_HIDDEN_EVENT = 'notification-prompt-hidden';
const PWA_PROMPT_VISIBLE_EVENT = 'pwa-prompt-visible';
const PWA_PROMPT_HIDDEN_EVENT = 'pwa-prompt-hidden';

const DISMISS_STORAGE_KEY = 'plick_notif_prompt_dismiss_count';
const MAX_DISMISSALS = 5;

export const NotificationPermissionRequest: React.FC = () => {
  const { permission, requestPermission } = usePushNotificationContext();
  const { isIOS, isStandalone } = usePwaInstall();
  const [isVisible, setIsVisible] = React.useState(() => {
    const count = parseInt(localStorage.getItem(DISMISS_STORAGE_KEY) || '0', 10);
    return count < MAX_DISMISSALS;
  });
  const [pwaPromptVisible, setPwaPromptVisible] = React.useState(false);

  const handleDismiss = React.useCallback(() => {
    const currentCount = parseInt(localStorage.getItem(DISMISS_STORAGE_KEY) || '0', 10);
    localStorage.setItem(DISMISS_STORAGE_KEY, String(currentCount + 1));
    setIsVisible(false);
  }, []);

  // Listen for PWA prompt visibility
  React.useEffect(() => {
    const handlePwaVisible = () => setPwaPromptVisible(true);
    const handlePwaHidden = () => setPwaPromptVisible(false);

    window.addEventListener(PWA_PROMPT_VISIBLE_EVENT, handlePwaVisible);
    window.addEventListener(PWA_PROMPT_HIDDEN_EVENT, handlePwaHidden);

    return () => {
      window.removeEventListener(PWA_PROMPT_VISIBLE_EVENT, handlePwaVisible);
      window.removeEventListener(PWA_PROMPT_HIDDEN_EVENT, handlePwaHidden);
    };
  }, []);

  React.useEffect(() => {
    // On iOS, we strictly require the app to be installed (standalone) before showing notification prompt
    if (isIOS && !isStandalone) {
      return;
    }

    const shouldShow = permission === 'default' && isVisible && !pwaPromptVisible;

    if (shouldShow) {
      window.dispatchEvent(new CustomEvent(NOTIFICATION_PROMPT_VISIBLE_EVENT));
    } else {
      window.dispatchEvent(new CustomEvent(NOTIFICATION_PROMPT_HIDDEN_EVENT));
    }

    // Cleanup: emit hidden event when component unmounts
    return () => {
      window.dispatchEvent(new CustomEvent(NOTIFICATION_PROMPT_HIDDEN_EVENT));
    };
  }, [permission, isVisible, pwaPromptVisible, isIOS, isStandalone]);

  // On iOS, strictly require standalone mode
  if (isIOS && !isStandalone) {
    return null;
  }

  if (permission === 'granted' || permission === 'denied' || !isVisible || pwaPromptVisible) {
    return null;
  }

  return (
    <>
      {/* Backdrop - blocks interaction with background */}
      <div className="fixed inset-0 bg-black bg-opacity-30 z-[1001]" onClick={handleDismiss} />

      {/* Notification Card */}
      <div className="fixed bottom-6 right-6 z-[1002] max-w-xs w-full">
        <div className="bg-color-white rounded-xl shadow-2xl border border-color-gray overflow-hidden transform transition-all duration-300 hover:scale-105">
          <div className="bg-color-primary h-1.5 w-full"></div>
          <div className="p-5 relative">
            <button
              onClick={handleDismiss}
              className="absolute top-2 right-2 text-color-gray_dark hover:text-color-gray_ultra_dark focus:outline-none"
              aria-label="Cerrar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 pt-1">
                <div className="p-2 bg-color-gray_ultra_light rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-color-primary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-color-primary_dark font-bold text-sm mb-1 pr-4">¿Activar notificaciones?</h3>
                <p className="text-color-gray_ultra_dark text-xs leading-relaxed mb-4">
                  Entérate al instante cuando recibas nuevos pedidos.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={requestPermission}
                    className="w-full bg-color-primary text-color-white text-xs font-bold py-2.5 px-4 rounded-lg shadow-md hover:bg-color-primary_dark transition-colors duration-200 focus:outline-none"
                  >
                    Sí, activar ahora
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
